import * as React from "react";
import { useTranslation } from "react-i18next";

// --- Icons ---
import { ChevronDownIcon } from "@/components/_organisms/tiptap/tiptap-icons/chevron-down-icon";
import { FontSizeIcon } from "@/components/_organisms/tiptap/tiptap-icons/font-size-icon";
// --- Tiptap UI ---
import {
  canSetFontSize,
  FontSizeButton,
  fontSizeOptions,
  type FontSizeType,
  isFontSizeActive,
} from "@/components/_organisms/tiptap/tiptap-ui/font-size-button";
// --- UI Primitives ---
import { Button, ButtonProps } from "@/components/_organisms/tiptap/tiptap-ui-primitive/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/_organisms/tiptap/tiptap-ui-primitive/dropdown-menu";
// --- Hooks ---
import { useTiptapEditor } from "@/hooks/use-tiptap-editor";
// --- Lib ---
import { type Editor } from "@tiptap/react";

export interface FontSizeDropdownProps extends Omit<ButtonProps, "type"> {
  /**
   * The TipTap editor instance.
   */
  editor?: Editor;
  /**
   * The font sizes to display in the dropdown.
   */
  fontSizes?: FontSizeType[];
  /**
   * Whether the dropdown should be hidden when no font sizes are available
   * @default false
   */
  hideWhenUnavailable?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}

export function canSetAnyFontSize(editor: Editor | null, fontSizes: FontSizeType[]): boolean {
  if (!editor) return false;
  return fontSizes.some((fontSize) => canSetFontSize(editor, fontSize.value));
}

export function isAnyFontSizeActive(editor: Editor | null, fontSizes: FontSizeType[]): boolean {
  if (!editor) return false;
  return fontSizes.some((fontSize) => isFontSizeActive(editor, fontSize.value));
}

export function getFilteredFontSizeOptions(availableFontSizes: FontSizeType[]): typeof fontSizeOptions {
  return fontSizeOptions.filter((option) => availableFontSizes.some((available) => available.value === option.value));
}

export function shouldShowFontSizeDropdown(params: {
  editor: Editor | null;
  fontSizes: FontSizeType[];
  hideWhenUnavailable: boolean;
  canSetAny: boolean;
}): boolean {
  const { hideWhenUnavailable, canSetAny } = params;

  if (hideWhenUnavailable && !canSetAny) {
    return false;
  }

  return true;
}

export function useFontSizeDropdownState(editor: Editor | null, availableFontSizes: FontSizeType[]) {
  const [isOpen, setIsOpen] = React.useState(false);

  const filteredFontSizes = React.useMemo(() => getFilteredFontSizeOptions(availableFontSizes), [availableFontSizes]);

  const canSetAny = canSetAnyFontSize(editor, availableFontSizes);
  const isAnyActive = isAnyFontSizeActive(editor, availableFontSizes);

  const handleOpenChange = React.useCallback((open: boolean, callback?: (isOpen: boolean) => void) => {
    setIsOpen(open);
    callback?.(open);
  }, []);

  return {
    isOpen,
    setIsOpen,
    filteredFontSizes,
    canSetAny,
    isAnyActive,
    handleOpenChange,
  };
}

export function FontSizeDropdown({
  editor: providedEditor,
  fontSizes = [
    { label: "8px", value: "8px" },
    { label: "10px", value: "10px" },
    { label: "12px", value: "12px" },
    { label: "14px", value: "14px" },
    { label: "16px", value: "16px" },
    { label: "18px", value: "18px" },
    { label: "20px", value: "20px" },
    { label: "24px", value: "24px" },
    { label: "28px", value: "28px" },
    { label: "32px", value: "32px" },
    { label: "36px", value: "36px" },
    { label: "48px", value: "48px" },
  ],
  hideWhenUnavailable = false,
  onOpenChange,
  ...props
}: FontSizeDropdownProps) {
  const { t } = useTranslation("common");
  const editor = useTiptapEditor(providedEditor);

  const { isOpen, filteredFontSizes, canSetAny, isAnyActive, handleOpenChange } = useFontSizeDropdownState(
    editor,
    fontSizes,
  );

  const getCurrentFontSize = React.useCallback(() => {
    const activeOption = filteredFontSizes.find((option) => isFontSizeActive(editor, option.value));
    return activeOption ? activeOption.value : "12px";
  }, [editor, filteredFontSizes]);

  const show = React.useMemo(() => {
    return shouldShowFontSizeDropdown({
      editor,
      fontSizes,
      hideWhenUnavailable,
      canSetAny,
    });
  }, [editor, fontSizes, hideWhenUnavailable, canSetAny]);

  const handleOnOpenChange = React.useCallback(
    (open: boolean) => handleOpenChange(open, onOpenChange),
    [handleOpenChange, onOpenChange],
  );

  if (!show || !editor || !editor.isEditable) {
    return null;
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={handleOnOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          data-style="ghost"
          data-active-state={isAnyActive ? "on" : "off"}
          aria-label={t("editor.fontSizeOptions")}
          tooltip={t("editor.fontSize")}
          {...props}
        >
          {getCurrentFontSize() ? (
            <span className="sm:inline">{getCurrentFontSize()}</span>
          ) : (
            <FontSizeIcon className="tiptap-button-icon" />
          )}
          <ChevronDownIcon className="tiptap-button-dropdown-small" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent>
        <DropdownMenuGroup>
          {filteredFontSizes.map((option) => (
            <DropdownMenuItem key={option.value} asChild>
              <FontSizeButton
                editor={editor}
                fontSize={option.value}
                text={option.label}
                hideWhenUnavailable={hideWhenUnavailable}
              />
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default FontSizeDropdown;
